import { useEffect, useState, useRef } from "react";
import { Link, NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { Bell, Radar, LogOut, Menu, X, Shield, Check, Share2, HelpCircle, Search } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { relativeTime } from "@/lib/format";
import { startTour, maybeAutoStartTour } from "@/lib/tour";
import { PlayerSearch } from "@/components/PlayerSearch";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { SourcesStrip } from "@/components/SourcesStrip";
import { toast } from "sonner";

const ROUTE_META = {
  "/feed": ["Live Feed", "Real-time NFL, NBA, NHL & MLB player status — injuries, scratches, trades & lineup changes, each source-cited and timestamped."],
  "/live": ["Live Lines", "Genuinely live NBA & NHL games with real sportsbook odds across US books — moneyline, spread and totals, straight from the market."],
  "/games": ["Games", "Every matchup with the information that moves it — injuries, starting goalies, recent form, weather and lines. Research only, not betting advice."],
  "/status": ["Injury & Status Report", "Every team's official injury designations across the NFL, NBA, NHL & MLB — Out, Doubtful, Questionable, Day-To-Day and IR, with injury type, expected return and an ESPN source link on every player."],
  "/practice": ["NFL Practice Report", "Wednesday–Friday practice participation — DNP, Limited and Full — the NFL's signature availability signal, from SportsDataIO's licensed feed."],
  "/parlay": ["Parlay Risk Scanner", "Drop players into a slip and get a Slip Health Index that flags open status concerns. Information only — never bet picks."],
  "/pipeline": ["Pipeline Telemetry", "See the five-agent ingestion pipeline that powers SidelineIQ — ingestion, normalization, classification and alerting in real time."],
  "/watchlist": ["Watchlists & Alerts", "Build named watchlists and get alerts the moment a player you follow changes status. Guests can save lists on-device, no sign-up."],
  "/alerts": ["Alert Center", "Every status change on the players and teams you follow — filter by unread, player or team, and mark them read as you go."],
  "/rulings": ["Official Rulings", "A permanent, searchable archive of the biggest official rulings across the NFL, NBA, NHL & MLB — ruled outs, late scratches, suspensions, trades and returns."],
  "/teams": ["Teams", "Browse every team across the NFL, NBA, NHL & MLB — full rosters, live availability and each team's recent official rulings."],
  "/pro": ["Pricing & Pro", "Go Pro for the real-time feed, instant alerts and bigger watchlists. Founder launch pricing while spots last."],
  "/partners": ["White-Label Partners", "Issue branded API keys so partners can embed your cited injury board or pull it via REST."],
  "/admin/email": ["Email Setup", "Verify your sending domain so alerts and digests reach real users."],
};

function setMeta(pathname) {
  const [label, desc] = ROUTE_META[pathname] || (pathname.startsWith("/players/")
    ? ["Player Profile", "Player availability, status timeline, form trends and cross-book line movement — source-cited on SidelineIQ."]
    : [null, "Player status intel across NFL, NBA, NHL & MLB — before the line moves."]);
  document.title = label ? `${label} · SidelineIQ` : "SidelineIQ — Player Status Intel";
  let m = document.querySelector('meta[name="description"]');
  if (!m) { m = document.createElement("meta"); m.setAttribute("name", "description"); document.head.appendChild(m); }
  m.setAttribute("content", desc);
}

const NAV = [
  { to: "/feed", label: "Feed" },
  { to: "/games", label: "Games" },
  { to: "/status", label: "Injuries" },
  { to: "/practice", label: "Practice" },
  { to: "/alerts", label: "Alerts" },
  { to: "/goalies", label: "Goalies" },
  { to: "/lineups", label: "Lineups" },
  { to: "/live", label: "Live Lines" },
  { to: "/rulings", label: "Rulings" },
  { to: "/teams", label: "Teams" },
  { to: "/parlay", label: "Parlay" },
  { to: "/watchlist", label: "Watchlist" },
  { to: "/pro", label: "Pro" },
];

function playChime() {
  if (localStorage.getItem("sq_alert_sound") === "off") return;
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const notes = [880, 1174.7];
    notes.forEach((f, i) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "sine";
      o.frequency.value = f;
      o.connect(g); g.connect(ctx.destination);
      const t = ctx.currentTime + i * 0.12;
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.18, t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.28);
      o.start(t); o.stop(t + 0.3);
    });
    setTimeout(() => ctx.close(), 800);
  } catch { /* audio unavailable */ }
}

function AlertsBell() {
  const { user } = useAuth();
  const [data, setData] = useState({ alerts: [], unread: 0 });
  const [ping, setPing] = useState(false);
  const prevUnread = useRef(null);

  const load = async () => {
    try {
      const r = await api.get("/alerts");
      setData(r.data);
      const u = r.data?.unread ?? 0;
      if (prevUnread.current != null && u > prevUnread.current) {
        playChime();
        setPing(true);
        setTimeout(() => setPing(false), 2500);
      }
      prevUnread.current = u;
    } catch { /* alerts poll failed; keep last data */ }
  };
  useEffect(() => {
    if (!user) return;
    load();
    const t = setInterval(() => { if (!document.hidden) load(); }, 12000);
    return () => clearInterval(t);
  }, [user]);

  if (!user) return null;

  const markAll = async () => { await api.post("/alerts/read-all"); load(); };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid="alerts-bell-btn" className="relative p-2 rounded-lg hover:bg-[#1C2438] transition-colors">
          <Bell size={18} className={`text-slate-300 ${ping ? "sq-bell-shake" : ""}`} />
          {data.unread > 0 && (
            <>
              {ping && <span className="absolute -top-0.5 -right-0.5 h-4 w-4 rounded-full bg-[#FF4D4D]/60 animate-ping" />}
              <span data-testid="alerts-unread-count" className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-[#FF4D4D] text-[10px] font-bold text-white flex items-center justify-center">
                {data.unread}
              </span>
            </>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 bg-[#0F1420] border-[#232D42] p-0">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#232D42]">
          <span className="font-heading font-semibold text-slate-100">Alerts</span>
          <button data-testid="alerts-mark-all-btn" onClick={markAll} className="text-xs text-[#00E5A3] hover:underline flex items-center gap-1">
            <Check size={12} /> Mark all read
          </button>
        </div>
        <div className="max-h-96 overflow-y-auto">
          {data.alerts.length === 0 && (
            <div className="px-4 py-8 text-center text-sm text-slate-500">No alerts yet. Add players to your watchlist.</div>
          )}
          {data.alerts.slice(0, 12).map((a) => (
            <Link key={a.id} to={`/players/${a.player_id}`} data-testid={`alert-item-${a.id}`}
              className={`block px-4 py-3 border-b border-[#232D42]/60 hover:bg-[#1C2438] ${a.read ? "opacity-60" : ""}`}>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded"
                  style={{ color: a.official ? "#38BDF8" : "#A855F7", background: a.official ? "rgba(56,189,248,0.1)" : "rgba(168,85,247,0.1)" }}>
                  {a.team_alert ? "team" : (a.official ? "official" : "reported")}
                </span>
                <span className="text-[10px] text-slate-500 font-mono ml-auto">{relativeTime(a.created_at)}</span>
              </div>
              <p className="text-sm text-slate-200 leading-snug">{a.headline}</p>
            </Link>
          ))}
        </div>
        <Link to="/alerts" data-testid="alerts-view-all" className="block px-4 py-2.5 text-center text-xs font-mono text-[#00E5A3] hover:bg-[#1C2438] border-t border-[#232D42]">
          View all in Alert Center →
        </Link>
      </PopoverContent>
    </Popover>
  );
}

async function shareApp() {
  const url = window.location.origin;
  const shareData = { title: "SidelineIQ", text: "Player status intel for smarter parlays — check out SidelineIQ:", url };
  try {
    if (navigator.share) { await navigator.share(shareData); return; }
    await navigator.clipboard.writeText(url);
    toast.success("Link copied — send it to friends & family!");
  } catch {
    // user cancelled share sheet or clipboard blocked — offer the raw URL
    toast("Copy this link to share", { description: url });
  }
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    if (location.pathname === "/feed") maybeAutoStartTour();
  }, [location.pathname]);

  useEffect(() => { setMeta(location.pathname); }, [location.pathname]);

  const navItems = user?.role === "admin" ? [...NAV, { to: "/partners", label: "Partners" }, { to: "/admin/email", label: "Email" }] : NAV;

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setSearchOpen((v) => !v); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="min-h-screen bg-[#080B11] text-slate-200">
      <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-[#080B11]/85 border-b border-[#232D42] h-16 px-4 sm:px-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/" data-testid="logo-link" className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-[#00E5A3] flex items-center justify-center">
              <Radar size={18} className="text-[#04140F]" />
            </div>
            <span className="font-heading font-extrabold text-lg tracking-tight text-slate-100">Sideline<span className="text-[#00E5A3]">IQ</span></span>
          </Link>
        </div>

        <nav className="hidden lg:flex items-center gap-1">
          {navItems.map((n) => (
            <NavLink key={n.to} to={n.to} data-testid={`nav-${n.to.slice(1)}`}
              className={({ isActive }) =>
                `px-3 py-2 rounded-lg text-sm font-medium transition-colors ${isActive ? "text-[#00E5A3] bg-[#00E5A3]/10" : "text-slate-400 hover:text-slate-100 hover:bg-[#1C2438]"}`
              }>
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button data-testid="search-btn-desktop" onClick={() => setSearchOpen(true)}
            className="hidden md:inline-flex items-center gap-2 h-9 pl-3 pr-2 rounded-lg bg-[#151B2B] border border-[#232D42] text-slate-400 hover:text-slate-200 hover:border-[#2A3550] transition-colors">
            <Search size={15} />
            <span className="text-sm">Search players</span>
            <kbd className="ml-1 text-[10px] font-mono px-1.5 py-0.5 rounded bg-[#0F1420] border border-[#232D42] text-slate-500">⌘K</kbd>
          </button>
          <button data-testid="search-btn" onClick={() => setSearchOpen(true)} title="Search players"
            className="md:hidden p-2 rounded-lg hover:bg-[#1C2438] text-slate-300">
            <Search size={18} />
          </button>
          <button data-testid="tour-btn" onClick={startTour} title="Take a quick tour"
            className="p-2 rounded-lg hover:bg-[#1C2438] text-slate-400 hover:text-slate-100">
            <HelpCircle size={18} />
          </button>
          <button data-testid="share-btn" onClick={shareApp} title="Share SidelineIQ"
            className="p-2 rounded-lg hover:bg-[#1C2438] text-slate-400 hover:text-[#00E5A3]">
            <Share2 size={18} />
          </button>
          <AlertsBell />
          {user ? (
            <div className="hidden sm:flex items-center gap-2">
              <span className="text-xs font-mono px-2 py-1 rounded-md bg-[#151B2B] border border-[#232D42] text-slate-300 flex items-center gap-1">
                {user.role === "admin" && <Shield size={11} className="text-[#00E5A3]" />}
                {(user.tier || "free").toUpperCase()}
              </span>
              <button data-testid="logout-btn" onClick={() => { logout(); navigate("/"); }}
                className="p-2 rounded-lg hover:bg-[#1C2438] text-slate-400" title="Logout">
                <LogOut size={17} />
              </button>
            </div>
          ) : (
            <Button data-testid="header-login-btn" onClick={() => navigate("/login")}
              className="hidden sm:inline-flex bg-[#00E5A3] hover:bg-[#00C98E] text-[#04140F] font-semibold rounded-lg h-9">
              Sign In
            </Button>
          )}
          <button data-testid="mobile-menu-btn" onClick={() => setOpen(!open)} className="lg:hidden p-2 rounded-lg hover:bg-[#1C2438] text-slate-300">
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </header>

      {open && (
        <div className="fixed top-16 left-0 right-0 z-40 lg:hidden bg-[#0F1420] border-b border-[#232D42] p-4 space-y-1">
          {navItems.map((n) => (
            <NavLink key={n.to} to={n.to} onClick={() => setOpen(false)}
              className={({ isActive }) => `block px-3 py-2.5 rounded-lg text-sm font-medium ${isActive ? "text-[#00E5A3] bg-[#00E5A3]/10" : "text-slate-300"}`}>
              {n.label}
            </NavLink>
          ))}
          {!user && (
            <Button onClick={() => { setOpen(false); navigate("/login"); }} className="w-full bg-[#00E5A3] text-[#04140F] font-semibold">Sign In</Button>
          )}
          {user && (
            <button onClick={() => { setOpen(false); logout(); navigate("/"); }} className="w-full text-left px-3 py-2.5 text-sm text-slate-300">Logout</button>
          )}
        </div>
      )}

      <PlayerSearch open={searchOpen} onOpenChange={setSearchOpen} />

      <main className="pt-16">
        <Outlet />
      </main>

      <footer className="border-t border-[#232D42] mt-16 px-6 py-8 max-w-[1600px] mx-auto">
        <p data-testid="disclaimer-footer" className="text-xs text-slate-500 font-mono leading-relaxed max-w-3xl">
          SidelineIQ provides availability status and source tracking only. No bet recommendations or financial
          advice provided. Every fact displays its source and last-updated time — verify official (league/team
          confirmed) vs reported (media/social, unconfirmed) before making your own decisions.
        </p>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-4 text-[11px] font-mono text-slate-500">
          <Link to="/about" data-testid="footer-about" className="hover:text-[#00E5A3]">About</Link>
          <Link to="/contact" data-testid="footer-contact" className="hover:text-[#00E5A3]">Contact</Link>
          <Link to="/terms" data-testid="footer-terms" className="hover:text-[#00E5A3]">Terms</Link>
          <Link to="/privacy" data-testid="footer-privacy" className="hover:text-[#00E5A3]">Privacy</Link>
          <Link to="/responsible-gaming" data-testid="footer-responsible-gaming" className="hover:text-[#00E5A3]">Responsible Gaming</Link>
          <Link to="/account" data-testid="footer-do-not-sell" className="hover:text-[#00E5A3]">Do Not Sell/Share My Info</Link>
          <span className="text-slate-600">18+ · 21+ where required</span>
          <span className="text-slate-600">Gambling problem? Call 1-800-GAMBLER</span>
        </div>
        <SourcesStrip />
      </footer>
    </div>
  );
}
